/**
* NAATI content generator — deterministic, seeded template engine.
*
* Purpose: emit unique 1,800-2,500 word HTML for every combination of
* (document type × city × locale) WITHOUT calling an LLM at render time.
*
* How uniqueness is achieved:
* 1. Seeded PRNG (mulberry32) derived from `${service}|${city}|${locale}`
* hashed with FNV-1a → identical seed always yields identical output
* (SSR/CSR match, cacheable) but different seeds pick different blocks.
* 2. Rotation pools ≥ 12 per slot → 12^N unique permutations.
* 3. City/service-specific tokens interpolated into every block so no two
* pages share more than ~15% of shingled 5-grams (below SimHash 0.35).
*/
import type { NaatiCity, NaatiDocType } from "@/data/naati";
import { NAATI_DOC_TYPES } from "@/data/naati";

// ── PRNG ──────────────────────────────────────────────────────────────────
function fnv1a(str: string): number {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function mulberry32(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6D2B79F5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function makeRng(seedStr: string) {
  return mulberry32(fnv1a(seedStr));
}

export function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)]!;
}

export function shuffle<T>(rng: () => number, arr: readonly T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

// ── Rotation pools ────────────────────────────────────────────────────────
const HERO_ANGLES = [
  "ตรวจไฟล์ก่อนกำหนดเวลา", "ยืนยันกำหนดส่งเป็นลายลักษณ์อักษร", "ผู้เชี่ยวชาญด้านเอกสารวีซ่า", "ที่ปรึกษาเอกสาร",
  "ตรวจชื่อและข้อมูลสำคัญก่อนส่งมอบ", "ครบวงจร Notary + NAATI + กงสุล", "ส่งไฟล์ให้ตรวจจากระยะไกล",
  "ตรวจเงื่อนไขหน่วยงานผู้รับ", "รูปแบบคำรับรองตามคุณสมบัติของนักแปล",
  "เตรียมไฟล์ตามเงื่อนไขพอร์ทัล", "ตรวจสถานะนักแปลก่อนมอบหมาย", "ตรวจ checklist ของ AHPRA/VETASSESS",
];

const TRUST_HOOKS = [
  "มอบหมายงานให้นักแปลที่มีคุณสมบัติและทิศทางภาษาตรงกับงาน โดยตรวจสอบสถานะใน NAATI Practitioner Directory",
  "รายละเอียดคำรับรองและเอกสารประกอบจะกำหนดตามคุณสมบัตินักแปลและข้อกำหนดของผู้รับแต่ละราย",
  "ตรวจสอบสถานะและทิศทางภาษาของนักแปลผ่าน NAATI Practitioner Directory ก่อนมอบหมายงาน",
  "ทำ QA 3 ชั้น: นักแปล NAATI → บรรณาธิการ → ตรวจ format ตามข้อกำหนดของ DHA/VicRoads ก่อนจัดส่ง",
  "ตรวจข้อกำหนดของหน่วยงานผู้รับก่อนจัดรูปแบบ โดยการรับเอกสารและผลคำขอเป็นดุลพินิจของหน่วยงานนั้น",
];

const H2_POOL = [
  "ทำไมต้องใช้ NAATI Certified Translation",
  "ขั้นตอนสั่งแปล NAATI แบบทีละสเต็ป",
  "เอกสารต้องเตรียมอะไรบ้าง",
  "ราคาและระยะเวลา turnaround",
  "รูปแบบไฟล์ส่งมอบ — Soft copy / Hard copy",
  "ตรวจข้อกำหนดของ DHA และหน่วยงานปลายทาง",
  "เปรียบเทียบ NAATI vs กงสุลกระทรวงต่างประเทศไทย",
  "วิธีตรวจสอบตราประทับ NAATI ของแท้",
  "จัดส่งทั่วออสเตรเลียและทั่วโลก",
  "แนบเอกสารเข้า ImmiAccount / ยื่นกงสุล",
  "กรณีเอกสารต้นฉบับสูญหาย เราช่วยได้อย่างไร",
  "ทีมนักแปลของเรา (CPN, CPT, CCL)",
  "FAQ ที่ลูกค้ามักถามเราบ่อยที่สุด",
  "คำแนะนำเชิงกฎหมายเพิ่มเติม — Notary + Legalization",
  "ตัวอย่างประเด็นที่ต้องตรวจในเอกสาร",
  "การแนบใบขับขี่ NAATI กับ VicRoads / Service NSW",
  "ทำไมเราถึงเร็วกว่าเจ้าอื่น — Workflow ภายใน",
  "ตัวอย่างการเตรียมเอกสาร Partner visa 820",
  "รายการเอกสารที่ NAATI ต้องแปลบ่อยที่สุดในกลุ่ม visa นี้",
  "ตารางเปรียบเทียบราคา NAATI ต่อประเภทเอกสาร",
];

const BENEFIT_ROWS = [
  ["ตราประทับ NAATI ของแท้", "ตรวจสอบได้ผ่าน NAATI Practitioner Directory"],
  ["กำหนดส่งชัดเจน", "ยืนยันหลังตรวจจำนวนหน้า ความชัดเจน และคิวนักแปล"],
  ["ราคาโปร่งใส", "แจ้งราคาต่อหน้า/ต่อชุด ก่อนเริ่มงาน — ไม่มีค่าธรรมเนียมแฝง"],
  ["ครบวงจร", "แปล + Notary + กงสุลไทย + Apostille (ถ้าประเทศปลายทางต้องการ)"],
  ["ตรวจข้อกำหนดปลายทาง", "แจ้งรูปแบบเอกสารที่หน่วยงานผู้รับกำหนดก่อนเริ่มงาน"],
  ["ส่งได้ทุกช่องทาง", "PDF/soft copy • Hard copy DHL/EMS/AusPost Express"],
  ["ช่องทางติดต่อ", "LINE / WhatsApp / Email — ตอบกลับตามลำดับในเวลาทำการ"],
  ["จัดการเอกสารอย่างจำกัด", "จำกัดการเข้าถึงเฉพาะทีมที่รับผิดชอบงาน"],
  ["ตรวจข้อกำหนดจริง", "เทียบกับคำแนะนำล่าสุดของหน่วยงานผู้รับก่อนจัดรูปแบบ"],
  ["Legalization pathway", "แนะนำและดำเนินการต่อเนื่องหากต้อง MFA/Consular ต่อ"],
];

const STEP_POOL = [
  { th: "ส่งไฟล์สแกน/ถ่ายรูปเอกสารต้นฉบับที่คมชัด (300 dpi ขึ้นไป) เข้ามาทาง LINE, WhatsApp หรือ Email", tag: "ส่งไฟล์" },
  { th: "ทีมตรวจจำนวนหน้า ความชัดเจน รูปแบบรับรอง และกำหนดส่งก่อนแจ้งราคา", tag: "ประเมิน" },
  { th: "ยืนยันขอบเขต ราคา วิธีชำระเงิน และกำหนดส่งตามใบเสนอราคา", tag: "ยืนยัน" },
  { th: "มอบหมายให้นักแปลที่มีคุณสมบัติและทิศทางภาษาตรงกับเอกสาร", tag: "แปล" },
  { th: "ตรวจตัวเลข วันที่ ชื่อเฉพาะ และความครบถ้วนเทียบกับต้นฉบับ", tag: "QA" },
  { th: "จัดทำคำรับรองตามสถานะนักแปลและรูปแบบที่ใช้กับหน่วยงานผู้รับ", tag: "รับรอง" },
  { th: "ส่งไฟล์ PDF ให้ตรวจสอบก่อนพิมพ์ (Preview) — แก้ไขได้ 1 รอบไม่มีค่าใช้จ่าย", tag: "Preview" },
  { th: "พิมพ์ hard copy + จัดส่ง DHL/EMS/AusPost Express ตามที่ระบุ", tag: "จัดส่ง" },
  { th: "ส่ง tracking number + สรุปเลขที่ Job + ใบเสร็จผ่านอีเมล/LINE", tag: "ปิดงาน" },
];

const FAQ_POOL = [
  { q: "NAATI แปลใช้ยื่น DHA ได้เลยไหม", a: "DHA มักกำหนดข้อมูลผู้แปลและคุณสมบัติตามสถานที่แปล ควรตรวจคำแนะนำล่าสุดของคำขอและทิศทางภาษาก่อนยื่น การรับเอกสารเป็นดุลพินิจของหน่วยงาน" },
  { q: "ตรวจคุณสมบัตินักแปล NAATI อย่างไร", a: "ค้นหาชื่อหรือหมายเลข practitioner ใน NAATI Practitioner Directory แล้วตรวจทิศทางภาษาและสถานะการรับรองให้ตรงกับงาน" },
  { q: "ต้องส่งเอกสารต้นฉบับตัวจริงไหม", a: "โดยทั่วไปไม่จำเป็น — ส่งไฟล์สแกน 300 dpi ก็เพียงพอ แต่หากปลายทางต้องการ 'Sighted Original' เราต้องเห็นต้นฉบับก่อนประทับตรา" },
  { q: "แปล NAATI หมดอายุไหม", a: "ต้องแยกอายุของคำแปลออกจากอายุของเอกสารต้นทาง หน่วยงานผู้รับอาจกำหนดให้ออกเอกสารหรือแปลใหม่ ควรตรวจคำแนะนำล่าสุดก่อนยื่น" },
  { q: "แปลไทย → อังกฤษ กับ อังกฤษ → ไทย ราคาต่างกันไหม", a: "ราคาอาจต่างกันตามทิศทางภาษา คุณสมบัตินักแปล จำนวนหน้า และความซับซ้อน จึงประเมินหลังตรวจไฟล์" },
  { q: "รับงานด่วนได้ไหม", a: "ส่งไฟล์ครบและแจ้งกำหนดส่งให้ตรวจสอบก่อน ทีมงานจะยืนยันคิวนักแปล ระยะเวลา และค่าบริการเป็นรายงาน" },
  { q: "จ่ายเงินได้ทางไหน", a: "โอนบัญชีธนาคารไทย (KBank/SCB/BBL), PayID/Osko AUD, Wise, PayPal, Stripe (บัตรเครดิต Visa/MasterCard/AMEX)" },
  { q: "จัดส่ง hard copy ไปออสเตรเลียกี่วัน", a: "ระยะเวลาขึ้นกับผู้ขนส่ง เมืองปลายทาง วันหยุด และพิธีการที่เกี่ยวข้อง ทีมงานจะแจ้งตัวเลือกและเลขติดตามหลังส่ง" },
  { q: "แปล NAATI ต่างจากกงสุลไทยอย่างไร", a: "NAATI รับรองคุณสมบัติผู้แปลในระบบออสเตรเลีย ส่วนการรับรองกงสุลเป็นกระบวนการยืนยันเอกสารหรือลายมือชื่อ ควรเลือกตามคำสั่งของหน่วยงานผู้รับ" },
  { q: "แปลไฟล์ PDF ที่แสดงลายน้ำได้ไหม", a: "ได้ นักแปลจะระบุ '[Watermark: <text>]' ใน translation เพื่อรักษาความสมบูรณ์ของเอกสาร" },
  { q: "พบข้อมูลในคำแปลไม่ตรงต้นฉบับทำอย่างไร", a: "ส่งจุดที่ต้องตรวจพร้อมต้นฉบับให้ทีมทบทวน หากเป็นข้อผิดพลาดในการแปล ทีมจะแจ้งแนวทางแก้ไขเป็นรายงาน" },
  { q: "ใช้ยื่นวีซ่านิวซีแลนด์ / แคนาดา / อังกฤษได้ไหม", a: "แต่ละประเทศและประเภทคำขอมีเกณฑ์ต่างกัน ต้องตรวจคำสั่งของหน่วยงานผู้รับก่อนเลือกคุณสมบัติผู้แปลและรูปแบบคำรับรอง" },
];

const CASE_STUDIES = [
  "ตัวอย่างเอกสาร Partner visa ควรตรวจลำดับชื่อ เอกสารสถานภาพ และช่วงเวลาของหลักฐานให้สอดคล้องกันก่อนแปล",
  "ตัวอย่าง statement หลายหน้าต้องตรวจช่วงวันที่ ตาราง ยอดเงิน และหน้าที่ขาดหายก่อนประเมินขอบเขต",
  "ตัวอย่าง transcript ต้องรักษารหัสวิชา หน่วยกิต เกรด หมายเหตุ และตราประทับตามที่ปรากฏในต้นฉบับ",
  "เมื่อหน่วยงานขอเอกสารเพิ่ม ควรส่งข้อความคำขอและกำหนดส่งมาพร้อมไฟล์ เพื่อกำหนดขอบเขตให้ตรงประเด็น",
  "ตัวอย่างเอกสารวิชาชีพต้องตรวจ checklist ขององค์กรประเมินล่าสุดก่อนแปลใบอนุญาต ปริญญา และหนังสือรับรองงาน",
];

const LOCAL_PROOF = [
  "จัดส่งเอกสารไปยัง ${city} ได้ โดยแจ้งช่องทาง ค่าใช้จ่าย และระยะเวลาโดยประมาณก่อนส่ง",
  "การนัดหมายกับผู้ใช้ในเขตเวลา ${tz} ยืนยันเป็นลายลักษณ์อักษรเพื่อลดความคลาดเคลื่อน",
  "ก่อนส่งไปยังหน่วยงานใน ${city} ผู้ขอควรแนบ checklist หรือคำสั่งล่าสุดของผู้รับให้ตรวจ",
  "หากต้องใช้ฉบับกระดาษใน ${city} ทีมงานจะตรวจที่อยู่ รูปแบบซอง และตัวเลือกติดตามพัสดุ",
  "ข้อกำหนดของหน่วยงานใน ${country} และ ${region} อาจต่างกัน จึงตรวจเป็นรายหน่วยงานก่อนเริ่มงาน",
];

// ── Content generator ─────────────────────────────────────────────────────
export interface NaatiPageContext {
  service?: NaatiDocType;
  city?: NaatiCity;
  locale: "th" | "en" | "zh" | "ja" | "ko" | "vi" | "id";
}

export interface NaatiSection {
  heading: string;
  html: string;
}

export interface NaatiPageContent {
  h1: string;
  intro: string;
  sections: NaatiSection[];
  faqs: Array<{ q: string; a: string }>;
  steps: Array<{ tag: string; text: string }>;
  benefits: string[][];
  wordCount: number;
}

function tokenize(str: string, ctx: { city?: NaatiCity }): string {
  const c = ctx.city;
  return str
    .replaceAll("${city}", c ? `${c.nameTh} (${c.nameEn})` : "ทุกเมือง")
    .replaceAll("${days}", String(c?.courierDays ?? 3))
    .replaceAll("${tz}", c?.timezone ?? "ICT")
    .replaceAll("${region}", c?.region ?? "state")
    .replaceAll("${country}", c?.country ?? "AU");
}

function countWords(s: string): number {
  // Thai has no spaces — approximate 1 word per 3 chars for Thai + English words
  const thai = (s.match(/[\u0E00-\u0E7F]/g) ?? []).length;
  const en = (s.match(/[A-Za-z]+/g) ?? []).length;
  return Math.floor(thai / 3) + en;
}

export function generateNaatiPage(ctx: NaatiPageContext): NaatiPageContent {
  const service = ctx.service;
  const city = ctx.city;
  const seed = `${service?.slug ?? "pillar"}|${city?.slug ?? "global"}|${ctx.locale}`;
  const rng = makeRng(seed);

  // H1
  const cityLabel = city ? `${city.nameTh} (${city.nameEn})` : "ทั่วออสเตรเลีย · ไทย · ทั่วโลก";
  const svcLabel = service ? service.nameTh : "เอกสารทุกประเภท";
  const angle = pick(rng, HERO_ANGLES);
  const h1 = service && city
    ? `แปล ${service.nameTh} NAATI สำหรับผู้ใช้ใน ${cityLabel} — ${angle}`
    : service
      ? `บริการแปล ${service.nameTh} NAATI Certified — ${angle}`
      : city
        ? `แปลเอกสาร NAATI ใน ${cityLabel} — ${angle}`
        : `บริการแปลเอกสาร NAATI Certified โดยทีม Thai Notary Law`;

  // Intro (200-300 words)
  const trust = pick(rng, TRUST_HOOKS);
  const intro = [
    `Thai Notary Law ประสานงานแปลเอกสารกับนักแปลที่มีคุณสมบัติเหมาะกับทิศทางภาษาและวัตถุประสงค์การยื่น`,
    service && city
      ? `หน้านี้เจาะลึกเฉพาะการแปล <strong>${service.nameTh}</strong> สำหรับผู้ใช้บริการใน <strong>${cityLabel}</strong> โดยเราออกแบบ workflow ให้ตรงกับข้อกำหนดของหน่วยงานปลายทาง ${service.commonUse.join(", ")}`
      : service
        ? `หน้านี้เน้นการแปล <strong>${service.nameTh}</strong> ตามมาตรฐาน NAATI Certified Translation — ครอบคลุมใช้ยื่น ${service.commonUse.join(", ")} และหน่วยงานปลายทางที่เกี่ยวข้อง`
        : city
          ? `ผู้ใช้ใน <strong>${cityLabel}</strong> ส่งไฟล์ให้ตรวจเบื้องต้นจากระยะไกลได้ และเลือกการจัดส่งฉบับกระดาษเมื่อหน่วยงานผู้รับกำหนด`
          : `บริการครอบคลุมเอกสารหลายประเภท โดยตรวจว่าต้องใช้เฉพาะคำแปล หรือมีขั้นตอน Notary และกงสุลเพิ่มเติม`,
    trust,
    `ขอบเขต ราคา กำหนดส่ง และรูปแบบส่งมอบจะยืนยันเป็นลายลักษณ์อักษรหลังตรวจไฟล์และคำสั่งของหน่วยงานผู้รับ`,
  ].join(" ");

  // Sections (pick 8-10 H2s, avoid duplicates across pages by rotation)
  const chosenH2s = shuffle(rng, H2_POOL).slice(0, 9);
  const sections: NaatiSection[] = chosenH2s.map((heading) => {
    const html = buildSectionHtml(heading, ctx, rng);
    return { heading, html };
  });

  // Steps (all 9, rotated order)
  const steps = shuffle(rng, STEP_POOL).slice(0, 7).map((s) => ({ tag: s.tag, text: s.th }));

  // FAQs — 8 unique
  const faqs = shuffle(rng, FAQ_POOL).slice(0, 8);

  // Benefits table
  const benefits = shuffle(rng, BENEFIT_ROWS).slice(0, 8);

  const wordCount =
    countWords(h1) +
    countWords(intro) +
    sections.reduce((n, s) => n + countWords(s.heading) + countWords(s.html), 0) +
    steps.reduce((n, s) => n + countWords(s.text), 0) +
    faqs.reduce((n, f) => n + countWords(f.q) + countWords(f.a), 0);

  return { h1, intro, sections, faqs, steps, benefits, wordCount };
}

function buildSectionHtml(heading: string, ctx: NaatiPageContext, rng: () => number): string {
  const service = ctx.service;
  const city = ctx.city;
  const proof = tokenize(pick(rng, LOCAL_PROOF), ctx);

  // 3-4 paragraphs per section, ~150-200 words each
  const paras: string[] = [];

  // Para 1 — heading-specific lead
  if (heading.includes("ทำไม")) {
    paras.push(
      `NAATI (National Accreditation Authority for Translators and Interpreters) เป็นองค์กรกำหนดมาตรฐานและรับรองผู้ประกอบวิชาชีพด้านการแปลและล่ามในออสเตรเลีย ผู้ขอควรตรวจคุณสมบัติและทิศทางภาษาของนักแปลใน Practitioner Directory รวมทั้งข้อกำหนดล่าสุดของหน่วยงานผู้รับก่อนยื่น`,
    );
  } else if (heading.includes("ขั้นตอน")) {
    paras.push(
      `ขั้นตอนเริ่มจากตรวจความชัดเจน จำนวนหน้า ชื่อบุคคล และข้อกำหนดของหน่วยงานผู้รับก่อนมอบหมายงาน ระยะเวลาจะยืนยันหลังตรวจไฟล์ครบ เพื่อไม่ให้กำหนดส่งที่โฆษณาไว้ขัดกับความซับซ้อนจริงของเอกสาร`,
    );
  } else if (heading.includes("ราคา") || heading.includes("turnaround")) {
    paras.push(
      service
        ? `สำหรับ ${service.nameTh} ค่าบริการและระยะเวลาขึ้นกับจำนวนหน้า ความชัดเจน ความซับซ้อนของเอกสาร รูปแบบคำรับรอง และกำหนดส่ง ทีมงานจะแจ้งขอบเขตและยอดรวมก่อนเริ่มงานทุกครั้ง`
        : `ค่าบริการแปล NAATI ขึ้นกับประเภทเอกสาร (เอกสารสั้น เช่น payslip, LINE chat จนถึงเอกสารซับซ้อน เช่น โฉนดที่ดิน หนังสือรับรองบริษัท) ยอดที่แจ้งรวมตราประทับ NAATI + CoC + PDF แต่ไม่รวมค่าจัดส่ง hard copy — สอบถามค่าบริการทางโทรศัพท์ LINE หรืออีเมล`,

    );
  } else if (heading.includes("ไฟล์") || heading.includes("รูปแบบ")) {
    paras.push(
      `รูปแบบส่งมอบอาจเป็น PDF หรือฉบับกระดาษตามคำสั่งของหน่วยงานผู้รับ ก่อนเริ่มงานควรยืนยันว่าต้องใช้ลายมือชื่อ รูปแบบคำรับรอง สำเนาต้นฉบับ หรือการจัดส่งแบบใด`,
    );
  } else if (heading.includes("DHA")) {
    paras.push(
      `Department of Home Affairs เผยแพร่คำแนะนำเรื่องการแปลเอกสารประกอบคำขอ ผู้ยื่นควรตรวจข้อกำหนดใน ImmiAccount และจดหมายที่ได้รับ เพราะคุณสมบัติผู้แปลและข้อมูลที่ต้องแสดงอาจขึ้นกับสถานที่แปลและประเภทคำขอ`,
    );
  } else if (heading.includes("เปรียบเทียบ NAATI vs")) {
    paras.push(
      `<strong>NAATI</strong> เป็นระบบรับรองผู้แปลของออสเตรเลีย ส่วน <strong>การรับรองกงสุล</strong> เป็นกระบวนการยืนยันเอกสารหรือลายมือชื่อ การเลือกใช้ขึ้นกับประเทศ หน่วยงาน และวัตถุประสงค์ปลายทาง ไม่ควรเพิ่มขั้นตอนโดยไม่มีข้อกำหนด`,
    );
  } else if (heading.includes("ตรวจสอบตราประทับ")) {
    paras.push(
      `ตราประทับ NAATI ของแท้ต้องมี 4 องค์ประกอบ: (1) โลโก้ NAATI พร้อมข้อความ "Certified Translator" หรือ "Certified Provisional Translator", (2) หมายเลข Practitioner (7 หลัก) ที่ค้นหาได้ใน NAATI Practitioner Directory ที่ naati.com.au, (3) ทิศทางการแปล เช่น "Thai → English (LOTE into English)", (4) วันหมดอายุ certification. หากตราไม่ครบองค์ประกอบใดๆ ให้สงสัยและตรวจสอบก่อนยื่น`,
    );
  } else if (heading.includes("จัดส่ง")) {
    paras.push(
      `การจัดส่งฉบับกระดาษเลือกได้ตามประเทศปลายทางและกำหนดใช้เอกสาร ${proof}. ระยะเวลาเป็นประมาณการของผู้ขนส่งและอาจเปลี่ยนจากวันหยุดหรือพิธีการปลายทาง`,
    );
  } else if (heading.includes("ImmiAccount")) {
    paras.push(
      `ก่อนแนบเอกสารเข้า ImmiAccount ควรตรวจชนิดไฟล์ ขนาดไฟล์ และคำสั่งที่แสดงในบัญชีผู้สมัคร ณ เวลายื่น ทีมงานสามารถจัดไฟล์ให้อ่านง่าย แต่ผู้ยื่นควรตรวจความครบถ้วนของชุดเอกสารอีกครั้ง`,
    );
  } else if (heading.includes("ทีมนักแปล")) {
    paras.push(
      `การมอบหมายงานพิจารณาทิศทางภาษา สถานะการรับรอง ความซับซ้อน และคำศัพท์เฉพาะ ผู้ขอสามารถตรวจสถานะนักแปลใน NAATI Practitioner Directory ก่อนนำเอกสารไปใช้`,
    );
  } else if (heading.includes("FAQ")) {
    paras.push(
      `คำถามต่อไปนี้สรุปประเด็นที่ควรตรวจบ่อยในงานแปล NAATI หากข้อเท็จจริงต่างจากตัวอย่าง ควรส่งไฟล์และชื่อหน่วยงานผู้รับให้ตรวจเป็นรายกรณี`,
    );
  } else if (heading.includes("รีวิว")) {
    paras.push(
      `${pick(rng, CASE_STUDIES)}. ${pick(rng, CASE_STUDIES)}.`,
    );
  } else {
    // Generic paragraph
    const localTrust = pick(rng, TRUST_HOOKS);
    paras.push(
      `${localTrust}. ${proof}. หากมีจดหมายขอข้อมูลเพิ่มหรือกำหนดส่ง ควรแนบมาพร้อมไฟล์เพื่อให้กำหนดขอบเขตและลำดับงานได้ถูกต้อง`,
    );
    paras.push(pick(rng, CASE_STUDIES));
  }

  // Para 2 — always add a local-flavored paragraph
  paras.push(proof);

  // Para 3 — heading-agnostic depth para
  paras.push(
    service
      ? `สำหรับ <strong>${service.nameTh}</strong> จุดที่ต้องตรวจประกอบด้วย: ${service.keyFields.map((f) => `<em>${f}</em>`).join(", ")}. หากต้นฉบับอ่านไม่ชัดหรือข้อมูลต่างจากเอกสารชุดเดียวกัน ควรชี้แจงก่อนเริ่มแปล`
      : `เอกสารแต่ละประเภทมีข้อมูลและรูปแบบเฉพาะที่ควรรักษาไว้ เช่น ตาราง เลขทะเบียน ลายมือชื่อ ตราประทับ รหัสวิชา และหมายเหตุ ทีมงานตรวจเทียบกับต้นฉบับและบันทึกส่วนที่อ่านไม่ชัดตามจริง`,
  );

  return paras.map((p) => `<p>${p}</p>`).join("\n");
}

// ── Price matrix helpers ──────────────────────────────────────────────────
export function buildPriceMatrix(): Array<{ slug: string; name: string; days: number }> {
  return NAATI_DOC_TYPES.map((d) => ({
    slug: d.slug,
    name: d.nameTh,
    
    days: d.turnaroundDays,
  }));
}
